import { Link, useRouterState } from '@tanstack/react-router'
import {
  Activity,
  CalendarDays,
  ChevronDown,
  FolderGit2,
  MessageCircle,
  Plus,
  Rocket,
  Server,
  StickyNote,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { cn } from '../components/cn'
import { t } from '../copy'
import { T } from '../copy/text'
import { groupRailConversations } from '../screens/lulu/rail'
import { useLuluConversations, useProjectConversations } from '../screens/lulu/queries'
import logoLockupDark from '../assets/logo-lockup.svg?url'
import logoLockupLight from '../assets/logo-lockup-on-light.svg?url'
import { useActivityList, useProjects, useToday } from './queries'
import { TOP_NAV, screenLabel, type ScreenKey } from './nav'

export const BRAND_WORDMARK = '24H NPC'

type SidebarMode = 'desktop' | 'mobile'

interface SidebarNavItem {
  readonly key: ScreenKey | 'today' | 'new-conversation'
  readonly path: string
  readonly label: string
  readonly Icon: LucideIcon
}

const MORE_NAV_KEYS = new Set<ScreenKey>(['code', 'hosts', 'skills'])

const NAV_ICONS: Record<ScreenKey, LucideIcon> = {
  chat: MessageCircle,
  project: FolderGit2,
  code: Rocket,
  hosts: Server,
  memo: StickyNote,
  skills: Users,
  timeline: Activity,
  dash: CalendarDays,
  sec: Server,
  models: Server,
  bridge: Server,
  vendor: Server,
  alert: Activity,
  team: Users,
  auth: Users,
  account: Users,
  server: Server,
  onboard: Server,
}

function sidebarPrimaryNavigation(): readonly SidebarNavItem[] {
  return [
    {
      key: 'new-conversation',
      path: '/lulu/new',
      label: t('lulu_workbench.rail.new_conversation'),
      Icon: Plus,
    },
    {
      key: 'today',
      path: '/today',
      label: t('nav.today.page.title'),
      Icon: CalendarDays,
    },
    ...TOP_NAV.filter((item) => item.key !== 'chat' && !MORE_NAV_KEYS.has(item.key)).map((item) => ({
      key: item.key,
      path: item.path,
      label: screenLabel(item.key),
      Icon: NAV_ICONS[item.key],
    })),
  ]
}

function sidebarMoreNavigation(): readonly SidebarNavItem[] {
  return TOP_NAV.filter((item) => MORE_NAV_KEYS.has(item.key)).map((item) => ({
    key: item.key,
    path: item.path,
    label: screenLabel(item.key),
    Icon: NAV_ICONS[item.key],
  }))
}

function navIsActive(pathname: string, item: SidebarNavItem): boolean {
  if (item.key === 'new-conversation') return pathname.startsWith('/lulu') || pathname.startsWith('/chat')
  if (item.key === 'today') return pathname === '/' || pathname === '/today'
  if (item.key === 'timeline') return pathname.startsWith('/activity') || pathname.startsWith('/timeline')
  return pathname.startsWith(item.path)
}

function useConversationGroups() {
  const conversations = useLuluConversations()
  const projects = useProjects()
  const projectIds = useMemo(
    () => (projects.data?.items ?? []).map((project) => project.id),
    [projects.data?.items],
  )
  const projectConversations = useProjectConversations(projectIds)
  const projectConversationData = projectConversations.map((query) => query.data)

  return useMemo(() => {
    const rows = [
      ...(conversations.data?.conversations ?? []),
      ...projectConversationData.flatMap((data) => data?.conversations ?? []),
    ]
    const seen = new Set<string>()
    const unique = rows.filter((conversation) => {
      if (seen.has(conversation.id)) return false
      seen.add(conversation.id)
      return true
    })

    return groupRailConversations(
      unique.map((conversation) => ({
        id: conversation.id,
        title: conversation.title,
        projectId: conversation.project_id ?? null,
        updatedAt: conversation.updated_at,
      })),
      projects.data?.items ?? [],
      t('lulu_workbench.rail.group_general'),
    )
  }, [conversations.data?.conversations, projectConversationData, projects.data?.items])
}

function TaskActivityIndicator({ compact, onNavigate }: { compact: boolean; onNavigate?: () => void }) {
  const today = useToday()
  const runningCount = today.data?.running?.length ?? 0
  const state = today.isPending || today.isError ? 'unknown' : runningCount > 0 ? 'running' : 'idle'
  const label = state === 'running'
    ? t('nav.status.running', { count: String(runningCount) })
    : t(state === 'idle' ? 'nav.status.idle' : 'nav.status.unknown')

  return (
    <Link
      to="/today"
      onClick={onNavigate}
      data-testid="task-activity-indicator"
      data-state={state}
      aria-label={label}
      title={label}
      className={cn(
        'relative ml-auto flex size-[28px] shrink-0 items-center justify-center rounded-npc-tag text-m-t3 transition-colors hover:bg-m-bg2 hover:text-m-t1',
        state === 'running' && 'bg-m-bg3 text-m-ok',
        state === 'unknown' && 'text-m-t4',
        compact && 'max-[1119px]:absolute max-[1119px]:left-[33px] max-[1119px]:top-[6px] max-[1119px]:size-[12px] max-[1119px]:rounded-full max-[1119px]:bg-m-bg1',
      )}
    >
      <Activity
        size={15}
        strokeWidth={2}
        aria-hidden="true"
        className={cn(compact && 'max-[1119px]:hidden')}
      />
      <span
        aria-hidden="true"
        className={cn(
          'absolute right-[3px] top-[3px] size-[6px] rounded-full border border-m-bg1',
          state === 'running' && 'bg-m-ok animate-npc-pulse',
          state === 'idle' && 'bg-m-line3',
          state === 'unknown' && 'bg-m-t4',
          compact && 'max-[1119px]:inset-0 max-[1119px]:m-auto max-[1119px]:size-[6px] max-[1119px]:border-0',
        )}
      />
    </Link>
  )
}

export function AppSidebarContent({
  mode,
  onNavigate,
  headerAction,
  footer,
}: {
  mode: SidebarMode
  onNavigate?: () => void
  headerAction?: ReactNode
  footer?: ReactNode
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const activity = useActivityList({ limit: 1 })
  const actionCount = activity.data?.requires_action_count ?? 0
  const groups = useConversationGroups()
  const activeConversationId = pathname.match(/^\/lulu\/c\/([^/]+)/)?.[1] ?? null
  const compact = mode === 'desktop'
  const primaryNavigation = sidebarPrimaryNavigation()
  const moreNavigation = sidebarMoreNavigation()
  const moreIsActive = moreNavigation.some((item) => navIsActive(pathname, item))
  const [moreExpanded, setMoreExpanded] = useState(moreIsActive)
  const moreNavigationId = `sidebar-more-navigation-${mode}`

  useEffect(() => {
    if (moreIsActive) setMoreExpanded(true)
  }, [moreIsActive])

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <div className="relative flex h-[52px] w-full min-w-0 shrink-0 items-center overflow-hidden border-b border-m-line1 px-[14px] min-[1120px]:px-[16px]">
        <Link
          to="/today"
          onClick={onNavigate}
          aria-label={BRAND_WORDMARK}
          className="flex max-w-full min-w-0 items-center overflow-hidden"
        >
          <img
            src={logoLockupDark}
            alt={BRAND_WORDMARK}
            className={cn('h-[22px] w-auto light:hidden', compact && 'max-[1119px]:hidden')}
          />
          <img
            src={logoLockupLight}
            alt={BRAND_WORDMARK}
            className={cn('hidden h-[22px] w-auto light:block', compact && 'max-[1119px]:light:hidden')}
          />
          {compact ? (
            <span
              aria-hidden="true"
              className="hidden size-8 items-center justify-center rounded-npc-tag bg-m-bg3 text-npc-caption font-semibold text-m-t1 max-[1119px]:flex"
            >
              24
            </span>
          ) : null}
        </Link>
        <TaskActivityIndicator compact={compact} onNavigate={onNavigate} />
        <div className={cn('shrink-0', mode === 'mobile' && 'mr-[38px]')}>
          {headerAction}
        </div>
      </div>

      <nav
        aria-label={t('ui.navigation')}
        className="w-full min-w-0 shrink-0 overflow-hidden px-[8px] py-[10px]"
      >
        {primaryNavigation
          .filter((item) => item.key === 'new-conversation')
          .map((item) => (
            <Link
              key={item.key}
              to={item.path}
              onClick={onNavigate}
              data-testid="sidebar-new-session"
              title={item.label}
              className={cn(
                'npc-sb-new',
                compact && 'max-[1119px]:justify-center max-[1119px]:px-0',
              )}
            >
              <item.Icon size={14} strokeWidth={1.5} className="shrink-0" aria-hidden="true" />
              <span className={cn('min-w-0 truncate', compact && 'max-[1119px]:hidden')}>{item.label}</span>
            </Link>
          ))}

        {primaryNavigation
          .filter((item) => item.key !== 'new-conversation')
          .map((item) => {
          const active = navIsActive(pathname, item)
          const badge = item.key === 'timeline' && actionCount > 0 ? actionCount : null
          return (
            <Link
              key={item.key}
              to={item.path}
              onClick={onNavigate}
              aria-current={active ? 'page' : undefined}
              title={item.label}
              className={cn(
                'npc-sb-nav mb-[1px]',
                active && 'is-active',
                compact && 'max-[1119px]:justify-center max-[1119px]:px-0',
              )}
            >
              <item.Icon size={15} strokeWidth={1.35} className="shrink-0" aria-hidden="true" />
              <span className={cn('min-w-0 flex-1 truncate', compact && 'max-[1119px]:hidden')}>
                {item.label}
              </span>
              {badge !== null ? (
                <span
                  data-testid="nav-activity-badge"
                  className={cn(
                    'shrink-0 rounded-full bg-m-bg4 px-[6px] py-[1px] text-npc-meta text-m-t2',
                    compact && 'max-[1119px]:hidden',
                  )}
                >
                  {badge}
                </span>
              ) : null}
            </Link>
          )
        })}

        <button
          type="button"
          aria-expanded={moreExpanded}
          aria-controls={moreNavigationId}
          onClick={() => setMoreExpanded((expanded) => !expanded)}
          title={t('nav.more')}
          className={cn(
            'npc-sb-nav mb-[1px] border-0 bg-transparent text-left',
            moreIsActive && 'is-active',
            compact && 'max-[1119px]:justify-center max-[1119px]:px-0',
          )}
        >
          <ChevronDown
            size={15}
            strokeWidth={1.9}
            aria-hidden="true"
            className={cn('shrink-0 transition-transform', moreExpanded && 'rotate-180')}
          />
          <span className={cn('min-w-0 flex-1 truncate', compact && 'max-[1119px]:hidden')}>
            <T id="nav.more" />
          </span>
        </button>

        {moreExpanded ? (
          <div id={moreNavigationId} className="w-full min-w-0 overflow-hidden pb-[2px]">
            {moreNavigation.map((item) => {
              const active = navIsActive(pathname, item)
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  title={item.label}
                  className={cn(
                    'npc-sb-nav npc-sb-nav-more mb-[1px]',
                    active && 'is-active',
                    compact && 'max-[1119px]:justify-center max-[1119px]:px-0',
                  )}
                >
                  <item.Icon size={14} strokeWidth={1.9} className="shrink-0" aria-hidden="true" />
                  <span className={cn('min-w-0 flex-1 truncate', compact && 'max-[1119px]:hidden')}>
                    {item.label}
                  </span>
                </Link>
              )
            })}
          </div>
        ) : null}
      </nav>

      <section
        aria-label={t('lulu_workbench.rail.recents')}
        className={cn(
          'flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden border-t border-m-line1 pt-[12px]',
          compact && 'max-[1119px]:hidden',
        )}
      >
        <p className="m-0 shrink-0 px-[16px] pb-[7px] text-npc-meta font-medium text-m-t4">
          <T id="lulu_workbench.rail.recents" />
        </p>
        <div
          data-testid="sidebar-recents-scroll"
          className="npc-scrollbar-hidden min-h-0 w-full min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-[8px] pb-[8px]"
        >
          {groups.map((group) => (
            <div key={group.projectId ?? '__general__'} className="w-full min-w-0 overflow-hidden pb-[8px]">
              <p className="m-0 truncate px-[8px] pb-[3px] pt-[5px] text-npc-meta text-m-t4">
                {group.name}
              </p>
              {group.conversations.map((conversation) => {
                const active = activeConversationId === conversation.id
                return (
                  <Link
                    key={conversation.id}
                    to="/lulu/c/$conversationId"
                    params={{ conversationId: conversation.id }}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    title={conversation.title}
                    data-testid="sidebar-conversation-row"
                    data-selected={active ? 'true' : 'false'}
                    className={cn('npc-sb-row', active && 'is-selected')}
                  >
                    <span aria-hidden="true" className="npc-sb-dot" />
                    <span className="min-w-0 flex-1 truncate">{conversation.title}</span>
                  </Link>
                )
              })}
            </div>
          ))}
        </div>
      </section>

      {footer}
    </div>
  )
}

export function DesktopSidebar({
  headerAction,
  footer,
}: {
  headerAction?: ReactNode
  footer?: ReactNode
}) {
  return (
    <aside
      data-testid="app-sidebar"
      className="sticky top-0 hidden h-screen w-[64px] min-w-0 shrink-0 overflow-hidden border-r border-m-line1 bg-m-bg1 min-[601px]:flex min-[1120px]:w-[236px]"
    >
      <AppSidebarContent mode="desktop" headerAction={headerAction} footer={footer} />
    </aside>
  )
}
